import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { taskAPI } from '../../utils/crmApi';
import { ArrowLeftIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const TaskTemplateDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [template, setTemplate] = useState(null);

    useEffect(() => {
        loadTemplate();
    }, [id]);

    const loadTemplate = async () => {
        try {
            setLoading(true);
            const response = await taskAPI.getTemplateById(id);
            if (response.data.success) {
                setTemplate(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load template:', error);
            toast.error('Failed to load template');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
            return;
        }

        try {
            await taskAPI.deleteTemplate(id);
            toast.success('Template deleted successfully');
            navigate('/task-templates');
        } catch (error) {
            console.error('Failed to delete template:', error);
            toast.error('Failed to delete template');
        }
    };

    const handleCreateTask = () => {
        navigate(`/tasks/new?template=${id}`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!template) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-foreground mb-4">Template not found</h1>
                    <p className="text-muted-foreground mb-6">The template you're looking for doesn't exist or has been deleted.</p>
                    <button
                        onClick={() => navigate('/task-templates')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Templates
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/task-templates')}
                        className="p-2 text-muted-foreground hover:text-muted-foreground"
                    >
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">{template.title}</h1>
                        <p className="text-sm text-muted-foreground capitalize">
                            {template.category} • {template.steps?.length || 0} steps • Used {template.usageCount || 0} times
                        </p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => navigate(`/task-templates/${id}/edit`)}
                        className="inline-flex items-center px-3 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Edit
                    </button>
                    <button
                        onClick={handleDelete}
                        className="inline-flex items-center px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
                    >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Delete
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Template Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Description */}
                    {template.description && (
                        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                            <h2 className="text-lg font-semibold text-foreground mb-3">Description</h2>
                            <p className="text-muted-foreground whitespace-pre-wrap">{template.description}</p>
                        </div>
                    )}

                    {/* Steps */}
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <h2 className="text-lg font-semibold text-foreground mb-4">Template Steps</h2>
                        <div className="space-y-4">
                            {template.steps?.map((step, index) => (
                                <div key={index} className="flex items-start space-x-4 p-4 bg-muted rounded-lg">
                                    <div className="flex-shrink-0 mt-1">
                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 text-sm font-medium rounded-full">
                                            {index + 1}
                                        </span>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-medium text-foreground mb-1">{step.title}</h4>
                                        {step.description && (
                                            <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                                        )}
                                        {step.estimatedDuration && (
                                            <p className="text-xs text-muted-foreground">
                                                Estimated: {step.estimatedDuration}
                                            </p>
                                        )}
                                        {step.requiredDocuments && step.requiredDocuments.length > 0 && (
                                            <div className="mt-2">
                                                <p className="text-xs font-medium text-foreground mb-1">Required Documents:</p>
                                                <ul className="text-xs text-muted-foreground list-disc list-inside">
                                                    {step.requiredDocuments.map((doc, docIndex) => (
                                                        <li key={docIndex}>{doc}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {step.links && step.links.length > 0 && (
                                            <div className="mt-2">
                                                <p className="text-xs font-medium text-foreground mb-1">Links:</p>
                                                <ul className="text-xs text-blue-600">
                                                    {step.links.map((link, linkIndex) => (
                                                        <li key={linkIndex}>
                                                            <a 
                                                                href={link.url} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="hover:underline"
                                                            >
                                                                {link.title || link.url}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                        <div className="space-y-3">
                            <button
                                onClick={handleCreateTask}
                                className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Create Task from Template
                            </button>
                            <button
                                onClick={() => navigate(`/task-templates/${id}/edit`)}
                                className="w-full inline-flex items-center justify-center px-4 py-2 border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground text-foreground rounded-lg hover:bg-muted transition-colors"
                            >
                                <PencilIcon className="h-4 w-4 mr-2" />
                                Edit Template
                            </button>
                        </div>
                    </div>

                    {/* Template Info */}
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Template Information</h3>
                        <dl className="space-y-3">
                            <div>
                                <dt className="text-sm font-medium text-muted-foreground">Category</dt>
                                <dd className="text-sm text-foreground capitalize">{template.category}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-muted-foreground">Steps</dt>
                                <dd className="text-sm text-foreground">{template.steps?.length || 0}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-muted-foreground">Usage Count</dt>
                                <dd className="text-sm text-foreground">{template.usageCount || 0}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                                <dd className="text-sm">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        template.isActive 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {template.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                                <dd className="text-sm text-foreground">
                                    {new Date(template.createdAt).toLocaleDateString()}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-muted-foreground">Last Updated</dt>
                                <dd className="text-sm text-foreground">
                                    {new Date(template.updatedAt).toLocaleDateString()}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskTemplateDetail;
